import { useDispatch } from '@lingoda/hooks';
import { Box, Button, Img, Paper, Typography, colorsPalette, useUiEvent } from '@lingoda/ui';
import { actionClick } from '@lingoda/analytics';
import { trans } from '@lingoda/i18n';
import { styled } from '@lingoda/ui/theme';
import { DialogComponent, showDialog } from '@lingoda/dialogs';
import { updatePreferences } from '@lingoda/students';
import year2023Svg from '../../assets/2023.svg';
import { paperGradient, recapTrackedEvents } from '../../constants';
import confettiSvg from './confetti.svg';

export const YearlyRecapBanner = () => {
    const dispatch = useDispatch();
    const addEvent = useUiEvent();

    const handleClickDismiss = () => {
        void addEvent(actionClick('Dismiss'), {
            metadata: {
                category: 'Yearly recap banner',
            },
        });
        dispatch(updatePreferences({ dismissedWidgets: { yearlyRecapBanner: true } }));
    };

    const handleShowRecapClick = () => {
        void addEvent(actionClick(recapTrackedEvents.click.LaunchWrapUp));
        dispatch(showDialog(DialogComponent.YearlyRecapDialog));
    };

    return (
        <PaperContainer elevation={0}>
            <TextsContainer>
                <Title variant="h1">{trans('your-wrap-up', {}, 'student-yearly-recap')}</Title>
                <Description variant="body2">
                    {trans('recap-banner-description', {}, 'student-yearly-recap')}
                </Description>
                <ButtonsContainer>
                    <Button
                        color="primary"
                        variant="contained"
                        sx={{ minWidth: (theme) => theme.spacing(19) }}
                        target="_blank"
                        onClick={handleShowRecapClick}
                    >
                        {trans('recap-open-btn', {}, 'student-yearly-recap')}
                    </Button>
                    <Button variant="text" onClick={handleClickDismiss}>
                        {trans('check-course-banner-dismiss-button', {}, 'student-today')}
                    </Button>
                </ButtonsContainer>
            </TextsContainer>
            <ImageContainer>
                <Img src={year2023Svg} />
            </ImageContainer>
        </PaperContainer>
    );
};

const PaperContainer = styled(Paper)(({ theme }) => ({
    display: 'flex',
    position: 'relative',
    justifyContent: 'space-between',
    width: '100%',
    height: '100%',
    color: colorsPalette.darkblueCustom.dark,
    background: `url(${confettiSvg}), ${paperGradient}`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: 0,
    borderRadius: 0,

    [theme.breakpoints.down('md')]: {
        flexDirection: 'column-reverse',
        alignItems: 'center',
        // on md show confetti not here but in top container
        background: paperGradient,
    },
}));

const TextsContainer = styled(Box)(({ theme }) => ({
    padding: theme.spacing(10.5, 0, 10.5, 7),
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    justifyContent: 'center',

    [theme.breakpoints.down('md')]: {
        maxWidth: 430,
        padding: theme.spacing(1, 2, 3, 2),
        alignItems: 'center',
    },
}));

const ButtonsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),

    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'center',
    },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [theme.breakpoints.down('md')]: {
        // on md show confetti here, not in main container
        background: `url(${confettiSvg})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
    },
}));

const Title = styled(Typography)(({ theme }) => ({
    ...theme.typography.h1,
    [theme.breakpoints.down('md')]: theme.typography.h2,
}));

const Description = styled(Typography)(({ theme }) => ({
    ...theme.typography.body2,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(4),

    [theme.breakpoints.down('md')]: {
        textAlign: 'center',
    },
}));
